// ECDSA P-256 keys for devices, the site and guardian-app holders. Signatures are IEEE P1363
// r||s (64 bytes), the WebCrypto native encoding, carried as base64url.
import { b64url, fromB64url } from "./bytes.ts";
import { canonicalBytes } from "./canonical.ts";

const ALG = { name: "ECDSA", namedCurve: "P-256" } as const;
const SIG = { name: "ECDSA", hash: "SHA-256" } as const;

/** Device keys are non-extractable: the private half can sign but never leave the device. */
export async function generateKeyPair(extractable = false): Promise<CryptoKeyPair> {
  return (await crypto.subtle.generateKey(ALG, extractable, ["sign", "verify"])) as CryptoKeyPair;
}

/** Public keys travel as the 65-byte uncompressed point, base64url. */
export async function exportPublicKey(key: CryptoKey): Promise<string> {
  return b64url((await crypto.subtle.exportKey("raw", key)) as ArrayBuffer);
}

export async function importPublicKey(raw: string): Promise<CryptoKey> {
  const bytes = fromB64url(raw);
  if (bytes.length !== 65 || bytes[0] !== 4) throw new Error("public key must be an uncompressed P-256 point");
  return crypto.subtle.importKey("raw", bytes as BufferSource, ALG, true, ["verify"]);
}

export async function exportPrivateJwk(key: CryptoKey): Promise<JsonWebKey> {
  return (await crypto.subtle.exportKey("jwk", key)) as JsonWebKey;
}

export async function importPrivateJwk(jwk: JsonWebKey, extractable = false): Promise<CryptoKey> {
  return crypto.subtle.importKey("jwk", jwk, ALG, extractable, ["sign"]);
}

export async function signBytes(key: CryptoKey, bytes: Uint8Array): Promise<string> {
  return b64url(await crypto.subtle.sign(SIG, key, bytes as BufferSource));
}

export async function verifyBytes(publicKey: CryptoKey | string, bytes: Uint8Array, sig: string): Promise<boolean> {
  try {
    const key = typeof publicKey === "string" ? await importPublicKey(publicKey) : publicKey;
    const s = fromB64url(sig);
    if (s.length !== 64) return false;
    return await crypto.subtle.verify(SIG, key, s as BufferSource, bytes as BufferSource);
  } catch {
    return false;
  }
}

export const signCanonical = (key: CryptoKey, value: unknown) => signBytes(key, canonicalBytes(value));
export const verifyCanonical = (pub: CryptoKey | string, value: unknown, sig: string) =>
  verifyBytes(pub, canonicalBytes(value), sig);
